import React from "react";
import { Box, Divider, Rating, useTheme } from "@mui/material";
import {
  ProjectRatingResponse,
  ProjectRatingDepartment,
} from "../../../../models/OpsSync/OpsSyncFeedbackForm";
import { Department } from "../../../../models/department/Department";
import CustomTypography from "../../../components/common/form/CustomTypography";
import ImageGalleryModal from "../../../components/common/imageGallery/ImageGalleryModal";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";

interface OpsSyncRatingExperienceProps {
  projectRatings?: ProjectRatingResponse[];
  departmentList?: Department[];
  maxHeight?: number;
  minHeight?: number;
}

interface ProcessedRating {
  overallRating: number;
  costIncurred: number;
  feedback: string;
  departments: { department: string; rating: number }[];
  files: string[];
}

const OpsSyncRatingExperience: React.FC<OpsSyncRatingExperienceProps> = ({
  projectRatings,
  departmentList,
  maxHeight,
  minHeight = window.innerHeight - 498,
}) => {
  const theme = useTheme();

  const [galleryState, setGalleryState] = React.useState<{
    open: boolean;
    images: { image_url: string; type: string }[];
    selectedIndex: number;
  }>({
    open: false,
    images: [],
    selectedIndex: 0,
  });

  const handleOpenGallery = (
    files: { image_url: string; type: string }[],
    index: number
  ) => {
    setGalleryState({
      open: true,
      images: files,
      selectedIndex: index,
    });
  };

  const handleCloseGallery = () => {
    setGalleryState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleNavigateGallery = (direction: "next" | "prev") => {
    setGalleryState((prev) => {
      const length = prev.images.length;
      let newIndex = prev.selectedIndex;
      if (direction === "next") {
        newIndex =
          prev.selectedIndex === length - 1 ? 0 : prev.selectedIndex + 1;
      } else {
        newIndex =
          prev.selectedIndex === 0 ? length - 1 : prev.selectedIndex - 1;
      }
      return { ...prev, selectedIndex: newIndex };
    });
  };

  const processRatingData = (type: number): ProcessedRating | null => {
    const rating = projectRatings?.find((r) => r.type === type);
    if (!rating) return null;

    const overall = rating.overall_rating ?? rating.rating ?? 0;

    const departments = (rating.departments ?? []).map((d: ProjectRatingDepartment) => {
      const dept = departmentList?.find((dl) => dl.id === d.department_id);
      return {
        department: dept?.name ?? `Department ${d.department_id}`,
        rating: d.rating,
      };
    });

    return {
      overallRating: overall,
      costIncurred: rating.cost_incurred ?? rating.cost_impact ?? 0,
      feedback: rating.feedback ?? "",
      departments,
      files: Array.isArray(rating.files) ? rating.files : [],
    };
  };

  const teamData = processRatingData(1);
  const clientData = processRatingData(2);
  const hasNoData = !teamData && !clientData;

  const renderSection = (
    title: string,
    data: ProcessedRating | null
  ) => {
    if (!data) return null;

    const galleryFiles = data.files.map((f) => ({
      image_url: f,
      type:
        /\.(mp4|webm|ogg|mov)$/i.test(f) || f.startsWith("video")
          ? "video"
          : "image",
    }));

    return (
      <Box sx={{ flex: "1 1 50%", minWidth: 0 }}>
        <Box sx={{ height: "100%", borderRadius: 2, padding: "0px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 1.5,
            }}
          >
            <CustomTypography
              variant="heading5"
              style={{
                fontWeight: 700,
                color: theme.palette.customColors.blackBorder,
                padding: "0px",
                margin: "0px",
              }}
            >
              {title}
            </CustomTypography>
            <Rating
              value={data.overallRating}
              readOnly
              size="medium"
              sx={{
                "& .MuiRating-iconFilled": {
                  color: theme.palette.customColors?.purple || "#9C27B0",
                },
              }}
            />
          </Box>

          {data.feedback && (
            <Box sx={{ mb: 1 }}>
              <CustomTypography
                variant="body2"
                style={{
                  color: theme.palette.customColors.greyTextColor,
                  fontSize: "14px",
                  lineHeight: "20px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.customColors.blackBorder,
                  }}
                >
                  Feedback:
                </Box>{" "}
                {data.feedback}
              </CustomTypography>
            </Box>
          )}

          <CustomTypography
            variant="body2"
            style={{
              color: theme.palette.customColors.greyTextColor,
              fontSize: "14px",
              lineHeight: "20px",
              marginBottom: "10px",
            }}
          >
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: theme.palette.customColors.blackBorder,
              }}
            >
              Cost Incurred:
            </Box>{" "}
            {data.costIncurred}
          </CustomTypography>



          {data.departments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <CustomTypography
                variant="body2"
                style={{
                  fontWeight: 600,
                  color: theme.palette.customColors.blackBorder,
                  fontSize: "14px",
                  marginBottom: "8px",
                }}
              >
                Department Ratings:
              </CustomTypography>
              {data.departments.map((dept, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >

                  <CustomTypography
                    variant="body2"
                    style={{
                      color: theme.palette.customColors.greyTextColor,
                      fontSize: "14px",
                      lineHeight: "20px",
                      minWidth: "80px"
                    }}
                  >
                    {dept.department}
                  </CustomTypography>
                  <Rating
                    value={dept.rating}
                    readOnly
                    size="small"
                    sx={{
                      "& .MuiRating-iconFilled": {
                        color: theme.palette.customColors?.purple || "#9C27B0",
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}

          {data.files.length > 0 && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {data.files.map((file: string, fileIndex: number) => {
                const isVideo =
                  /\.(mp4|webm|ogg|mov)$/i.test(file) || file.startsWith("video");

                return isVideo ? (
                  <Box
                    key={`video-${fileIndex}`}
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 1,
                      background:
                        theme.palette.customColors.greyBodyBackground,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      border: "2px solid transparent",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                    onClick={() => handleOpenGallery(galleryFiles, fileIndex)}
                  >
                    <VideoLibraryIcon
                      sx={{
                        fontSize: 40,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    key={`img-${fileIndex}`}
                    component="img"
                    src={file}
                    alt={`Feedback ${fileIndex}`}
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 1,
                      objectFit: "cover",
                      cursor: "pointer",
                      border: "2px solid transparent",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                    onClick={() => handleOpenGallery(galleryFiles, fileIndex)}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: 1,
        "& .css-e473nu": {
          padding: "0px !important",
        },
        "@media (max-width: 768px)": {
          "& .experience-container": {
            minWidth: "600px",
          },
        },
      }}
    >
      <ImageGalleryModal
        images={galleryState.images}
        open={galleryState.open}
        selectedIndex={galleryState.selectedIndex}
        onClose={handleCloseGallery}
        onNavigate={handleNavigateGallery}
      />
      {hasNoData ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxHeight: maxHeight,
            minHeight: minHeight,
          }}
        >
          <CustomTypography
            variant="body2"
            fontWeight={700}
            color={theme.palette.customColors.greyTextColor}
          >
            FEEDBACK NOT AVAILABLE!
          </CustomTypography>
        </Box>
      ) : (
        <Box
          className="experience-container"
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: "column",
            justifyContent: "start",
            maxHeight: maxHeight,
            minHeight: minHeight,
          }}
        >
          {teamData && (
            <>
              {renderSection("Team Experience", teamData)}
              {clientData && (
                <Divider
                  sx={{
                    marginBottom: "20px",
                    borderBottomWidth: "3px",
                    borderColor:
                      theme.palette.customColors.greyBodyBackground,
                  }}
                />
              )}
            </>
          )}
          {clientData && renderSection("Client Experience", clientData)}
        </Box>
      )}
    </Box>
  );
};

export default OpsSyncRatingExperience;
